import axios from 'axios'
import {BaseApi} from './BaseApi'

// ============================================================================
// TYPES (mirror msp-service payroll/wotc-export DTOs)
// ============================================================================

export interface WotcFileResult {
  fileName: string
  content: string
  recordCount: number
  fileType: 'employee' | 'unitList' | 'payroll'
}

export interface WotcValidationIssue {
  category: string
  message: string
  entityName?: string
  entityId?: string
  severity: 'error' | 'warning'
}

export interface WotcValidationReport {
  hasErrors: boolean
  errors: WotcValidationIssue[]
  warnings: WotcValidationIssue[]
  validatedAt: string
}

export interface WotcExportResponse {
  success: boolean
  exportId: string | null
  files: WotcFileResult[]
  validationReport: WotcValidationReport
  generatedAt: string
}

export interface WotcGenerateParams {
  /** Picked calendar day as 'YYYY-MM-DD' (backend normalizes to whole UTC days) */
  startDate: string
  /** Picked calendar day as 'YYYY-MM-DD', inclusive */
  endDate: string
  /** When true the backend prefixes file names with TEST_ per the ADP test spec */
  isTest?: boolean
}

// ============================================================================
// API SERVICE
// ============================================================================

export class WotcApi extends BaseApi {
  /**
   * Generate the three ADP WOTC files (Employee, Unit List, Payroll) across all
   * EORs. Platform-admin only — the backend hard-gates the endpoint.
   *
   * Returns file contents inline; a blocked export (validation errors) rejects
   * with a 400 whose body carries the validation report — see
   * extractWotcErrorReport.
   */
  async generateExport(params: WotcGenerateParams): Promise<WotcExportResponse> {
    const response = await this.post<WotcExportResponse>('/payroll/wotc-export/generate', params)
    return response.data
  }
}

/**
 * Pull a human message + the validation report (when present) out of a failed
 * generate call. A blocked export responds 400 with
 * `{message: 'Pre-export validation failed', validationReport}`.
 */
export const extractWotcErrorReport = (
  error: unknown,
): {message: string; validationReport?: WotcValidationReport} => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | {message?: string; validationReport?: WotcValidationReport}
      | undefined
    if (data && (data.message || data.validationReport)) {
      return {
        message: data.message || 'WOTC export failed',
        validationReport: data.validationReport,
      }
    }
    return {message: error.message || 'WOTC export failed'}
  }
  if (error instanceof Error) {
    return {message: error.message}
  }
  return {message: 'WOTC export failed'}
}

// Export singleton instance
export const wotcApi = new WotcApi()
